import { useId, type InputHTMLAttributes } from 'react'
import { cn } from '../lib/cn.js'

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string
  description?: string
}

export function Switch({ className, label, description, id, ...props }: SwitchProps) {
  const autoId = useId()
  const switchId = id ?? autoId

  return (
    <label htmlFor={switchId} className={cn('flex cursor-pointer items-start gap-3', className)}>
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input type="checkbox" id={switchId} className="peer sr-only" {...props} />
        <span
          className={cn(
            'block h-6 w-11 rounded-full bg-border-strong transition-colors',
            'peer-checked:bg-primary peer-disabled:opacity-50',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-ring peer-focus-visible:outline-offset-2',
          )}
          aria-hidden
        />
        {/* transform لا ينعكس في RTL، لكن inset-inline-start ينعكس — لذا نحرّك المقبض به */}
        <span
          className={cn(
            'pointer-events-none absolute top-0.5 start-0.5 size-5 rounded-full bg-white shadow-sm',
            'transition-[inset-inline-start] duration-200 ease-[var(--ease-out-quint)]',
            'peer-checked:start-[1.375rem]',
          )}
          aria-hidden
        />
      </span>

      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block text-sm font-medium text-fg">{label}</span>}
          {description && <span className="block text-xs text-fg-subtle">{description}</span>}
        </span>
      )}
    </label>
  )
}
